import {
    Badge, Box, Chip, Divider, IconButton, List, ListItem,
    ListItemIcon, ListItemText, Menu, Typography,
} from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NotifyService from "services/NotifyService";
import { IconBell } from "components/icons/TablerIcons";
import { socket } from "utils/socket";
import notification from 'assets/sound/notification.mp3';
import { Howl } from 'howler';
import { ChangeContext } from 'components/useContext/ChangeContext';
import {
    VerifiedUser, AlternateEmail, ChatBubbleOutline,
    NotificationsNone, SystemUpdateAlt, SettingsOutlined,
    DoneAll,
} from '@material-ui/icons';
import NotificationSettings from './NotificationSettings';

const FILTERS = [
    { key: 'all', label: 'All', icon: <NotificationsNone style={{ fontSize: 16 }} /> },
    { key: 'mentions', label: 'Mentions', icon: <AlternateEmail style={{ fontSize: 16 }} /> },
    { key: 'messages', label: 'Messages', icon: <ChatBubbleOutline style={{ fontSize: 16 }} /> },
];

const typeIcons = {
    mention: <AlternateEmail style={{ fontSize: 18, color: '#1565C0' }} />,
    project_mention: <AlternateEmail style={{ fontSize: 18, color: '#1565C0' }} />,
    message: <ChatBubbleOutline style={{ fontSize: 18, color: '#2E7D32' }} />,
    group_message: <ChatBubbleOutline style={{ fontSize: 18, color: '#2E7D32' }} />,
    update: <SystemUpdateAlt style={{ fontSize: 18, color: '#E65100' }} />,
    calendar: <NotificationsNone style={{ fontSize: 18, color: '#FF844B' }} />,
};

const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const d = new Date(timestamp);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const Notifications = (props) => {
    const { setSendingUser, classes, fromWhereItIsInvoked } = props;
    const {
        count,
        setCount,
        setLastMessageReceived,
    } = useContext(ChangeContext);
    const [notifications, setNotifications] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all');
    const [settingsOpen, setSettingsOpen] = useState(false);
    const navigate = useNavigate();
    const [playSound, setPlaySound] = useState(false);

    const onStream = () => {
        socket.connect();

        socket.on("notifications", (data) => {
            if (data.notify) {
                setPlaySound(true);
                getNotifications();
            }
        });
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNotify = async (item) => {
        setAnchorEl(null);
        if (item.type_notify === "mention" && fromWhereItIsInvoked === 'header') {
            navigate(`/Edit/Project/${item.projectId}/${item.ref_id}`);
        } else if (item.type_notify === "mention" && fromWhereItIsInvoked === 'detailProject') {
            navigate(`/Edit/Project/${item.projectId}/${item.ref_id}`);
            navigate(0);
        } else if (item.type_notify === "project_mention") {
            navigate(`/Edit/Project/${item.projectId}`);
        } else if (item.type_notify === "calendar") {
            navigate('/Calendar');
            navigate(0);
        } else if (item.type_notify === "update") {
            navigate('/Update/List');
        } else if (item.is_group) {
            setSendingUser(item?.group_user_id);
            navigate(`/Chat/Messenger`);
        } else {
            setSendingUser(item?.senderId);
            navigate(`/Chat/Messenger`);
        }
        const data = { id: item.id, readAll: false };
        const res = await NotifyService.updateNotify(data);
        if (res.status === "success") {
            getNotifications();
        }
    };

    const getNotifications = async (filter) => {
        const params = {};
        const f = filter || activeFilter;
        if (f && f !== 'all') params.type = f;
        const res = await NotifyService.getNotify(params);
        if (res.status === "success") {
            try {
                const lastNotify = res?.data?.notifications?.find((msg) => msg.type_notify === 'message');
                setLastMessageReceived(lastNotify);
            } catch (err) {
                console.log(err);
            }
            setCount(res?.data?.count);
            setNotifications(res?.data?.notifications || []);
        } else {
            setNotifications([]);
        }
    };

    const handleFilterChange = (key) => {
        setActiveFilter(key);
        getNotifications(key);
    };

    const handleMarkAllRead = async () => {
        await NotifyService.markAllRead();
        setCount(0);
        getNotifications();
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const soundPlay = (src) => {
        const sound = new Howl({ src, html5: true });
        sound.play();
    };

    const updateNotificationList = async () => {
        try {
            await NotifyService.updateNotificationList();
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        getNotifications();
        onStream();
    }, []);

    useEffect(() => {
        if (playSound) {
            soundPlay(notification);
            setPlaySound(false);
        }
    }, [playSound]);

    useEffect(() => {
        if (Boolean(anchorEl) && count > 0) {
            setCount(0);
            updateNotificationList();
        }
    }, [anchorEl]);

    return (
        <>
            <Badge badgeContent={count} style={{ marginTop: 0 }} color="error" overlap="rectangular">
                <IconBell
                    width={22}
                    height={22}
                    style={{ color: "rgba(47, 43, 61, 0.7)", cursor: "pointer" }}
                    aria-controls="notify"
                    aria-haspopup="true"
                    onClick={handleClick}
                />
            </Badge>
            <Menu
                id="notify"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                    style: {
                        maxHeight: 520,
                        width: 400,
                        padding: 0,
                    },
                }}
            >
                {/* Header */}
                <Box px={2} pt={1.5} pb={0.5} display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#0D3F67' }}>
                        Notifications
                    </Typography>
                    <Box>
                        <IconButton size="small" title="Mark all as read" onClick={handleMarkAllRead}>
                            <DoneAll style={{ fontSize: 20 }} />
                        </IconButton>
                        <IconButton size="small" title="Settings" onClick={() => { setAnchorEl(null); setSettingsOpen(true); }}>
                            <SettingsOutlined style={{ fontSize: 20 }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* Filter chips */}
                <Box px={2} pb={1} display="flex" style={{ gap: 6 }}>
                    {FILTERS.map((f) => (
                        <Chip
                            key={f.key}
                            icon={f.icon}
                            label={f.label}
                            size="small"
                            onClick={() => handleFilterChange(f.key)}
                            variant={activeFilter === f.key ? 'default' : 'outlined'}
                            style={{
                                backgroundColor: activeFilter === f.key ? '#0D3F67' : 'transparent',
                                color: activeFilter === f.key ? '#fff' : '#0D3F67',
                                fontWeight: 600,
                                fontSize: 12,
                            }}
                        />
                    ))}
                </Box>

                <Divider />

                {/* Notification list */}
                <List style={{ padding: 0, overflowY: 'auto', maxHeight: 400 }}>
                    {notifications.length > 0
                        ? notifications.map((item, index) => (
                            <ListItem
                                key={index}
                                button
                                onClick={() => handleNotify(item)}
                                style={{
                                    backgroundColor: item.read_notify === 0 ? 'rgba(255, 132, 75, 0.06)' : '#fff',
                                    paddingTop: 8,
                                    paddingBottom: 8,
                                }}
                            >
                                <ListItemIcon style={{ minWidth: 36 }}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        style={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: 8,
                                            backgroundColor: item.type_notify === 'mention' || item.type_notify === 'project_mention'
                                                ? '#E3F2FD'
                                                : item.type_notify === 'message' || item.type_notify === 'group_message'
                                                    ? '#E8F5E9'
                                                    : '#FFF3E0',
                                        }}
                                    >
                                        {typeIcons[item.type_notify] || <NotificationsNone style={{ fontSize: 18 }} />}
                                    </Box>
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                            <Typography
                                                variant="body2"
                                                style={{
                                                    fontWeight: item.read_notify === 0 ? 700 : 500,
                                                    color: '#0D3F67',
                                                    fontSize: 13,
                                                    flex: 1,
                                                }}
                                            >
                                                {item.type_notify === 'update' ? (
                                                    <>{item.sender} <VerifiedUser style={{ width: 14, verticalAlign: 'middle' }} /></>
                                                ) : (
                                                    item.sender
                                                )}
                                            </Typography>
                                            {item.read_notify === 0 && (
                                                <Box
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        backgroundColor: '#EA5455',
                                                        marginLeft: 8,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    }
                                    secondary={
                                        <>
                                            <Typography component="span" variant="body2" style={{ color: 'rgba(47,43,61,0.7)', fontSize: 12 }}>
                                                {item.projectName ? `${item.projectName} — ` : ''}{item.message}
                                            </Typography>
                                            <br />
                                            <Typography component="span" variant="caption" style={{ color: 'rgba(47,43,61,0.4)' }}>
                                                {formatTime(item.created)}
                                            </Typography>
                                        </>
                                    }
                                />
                            </ListItem>
                        ))
                        : (
                            <Box py={4} display="flex" flexDirection="column" alignItems="center">
                                <NotificationsNone style={{ fontSize: 40, color: '#ccc' }} />
                                <Typography variant="body2" style={{ color: '#999', marginTop: 8 }}>
                                    No notifications
                                </Typography>
                            </Box>
                        )
                    }
                </List>
            </Menu>

            <NotificationSettings open={settingsOpen} onClose={() => setSettingsOpen(false)} />
        </>
    );
};

export default Notifications;
